// Add these type declarations at the top of the file
declare global {
    interface Window {
        additives?: {
            companySettings?: {
                complianceModules?: {
                    ism?: {
                        Enabled?: boolean;
                    };
                };
            };
        };
    }
}

import { firebaseImport, db } from "../../../config/main_config";
import { waitForWindowProperty } from "../../utils/routerAdditives.util";
import { waitForCompanyID } from "../../utils/waitForCompanyID.util";
import { Timestamp } from 'firebase/firestore';

export namespace NotificationSystem {
    export interface Notification {
        id: string;  // Unique ID for each notification
        title: string;
        icon?: string;  // Icon for the notification
        body: string;
        timestamp: Date;
        read?: boolean;  // Add a read flag to track if a notification has been read
        userID?: string | null;  // User ID to show notification only to specific user
        link?: string;
        deletedAt?: Timestamp | null;
    }

    async function initialise(): Promise<void> {
        // Don't remove, this is for future setup or initialization logic
    }

    async function buildNotificationCenter(customerID: string | null = null): Promise<void> {
        // This is where you'd inject HTML or UI components if needed
    }

    async function checkForCompanyID(customerID: string | null = null): Promise<string | undefined> {
        if (!customerID) {
            customerID = await waitForCompanyID();
            if (!customerID) {
                console.error('Customer ID not found');
                return;
            }
            return customerID;
        }
        return customerID;
    }

    export async function addNotification(customerID: string | null = null, notification: Notification, targetUserID?: string | null): Promise<void> {
        const companyID = await checkForCompanyID(customerID);

        if (!companyID) {
            console.error('Customer ID not found');
            return;
        }

        // console.log('Company Id', companyID);

        const companyRef = db.collection('customers').doc(companyID);
        const tenancyNotificationsRef = companyRef.collection('notifications').doc('tenancyNotifications');

        try {
            // Check if the tenancyNotifications document exists
            const docSnapshot = await tenancyNotificationsRef.get();

            // If the document doesn't exist, create it with a dummy notification to initialize
            if (!docSnapshot.exists) {
                await tenancyNotificationsRef.set({
                    notifications: [
                        {
                            id: `${new Date().getTime()}`, // Generate a unique ID
                            title: 'Initial Notification',
                            body: 'This is an initial notification to create the document.',
                            timestamp: new Date(),
                            read: false
                        },
                    ],
                });
                // console.log('Tenancy notifications document created');
            }

            // Add the actual notification with a unique ID
            await tenancyNotificationsRef.update({
                notifications: firebaseImport.firestore.FieldValue.arrayUnion({
                    ...notification,
                    id: `${new Date().getTime()}`, // Ensure each notification has a unique ID
                    read: false,  // Mark it as unread initially
                    userID: targetUserID || notification.userID || null  // Set user ID for user-specific notifications
                }),
            });
            console.log('Notification added successfully');
        } catch (error) {
            console.error('Error adding notification:', error);
        }
    }

    export async function clearNotification(customerID: string | null, notificationId: string): Promise<void> {
        const companyID = await checkForCompanyID(customerID);

        if (!companyID) {
            console.error('Customer ID not found');
            return;
        }

        const tenancyNotificationsRef = db.collection('customers')
            .doc(companyID)
            .collection('notifications')
            .doc('tenancyNotifications');

        try {
            // First get the current notifications
            const doc = await tenancyNotificationsRef.get();
            if (!doc.exists) {
                console.log('No notifications found');
                return;
            }

            const data = doc.data();
            if (!data?.notifications) {
                console.log('No notifications array found');
                return;
            }

            // Find and update the specific notification
            const updatedNotifications = data.notifications.map((notification: any) => {
                if (notification.id === notificationId) {
                    return {
                        ...notification,
                        deletedAt: firebaseImport.firestore.Timestamp.now()
                    };
                }
                return notification;
            });

            // console.log('Updated notifications:', updatedNotifications);
            // Update the entire notifications array with the modified notification
            await tenancyNotificationsRef.set({
                notifications: updatedNotifications
            }, { merge: true });
            
            console.log('Notification marked as deleted');
        } catch (error) {
            console.error('Error updating notification:', error);
        }
    }

    // Helper function to check if a lambda notification should be shown to the current user
    async function shouldShowLambdaNotification(notif: Notification, currentUserID: string, customerID: string): Promise<boolean> {
        if (notif.userID !== 'lambda-service-account' || !notif.link) {
            return false;
        }

        try {
            // Extract the AI suggestion ID from the link
            // Link format: customers/{customerID}/AI/assurance/suggestions/{suggestionId}
            const linkParts = notif.link.split('/');
            const suggestionId = linkParts[linkParts.length - 1];
            
            if (!suggestionId) {
                console.log('No suggestion ID found in link:', notif.link);
                return false;
            }

            // console.log(`Checking lambda notification ${suggestionId} for user ${currentUserID}`);

            // Look up the AI suggestion document to get the initiated_by field
            const suggestionDocRef = db.collection('customers')
                .doc(customerID)
                .collection('AI')
                .doc('assurance')
                .collection('suggestions')
                .doc(suggestionId);
            
            const suggestionDoc = await suggestionDocRef.get();
            
            if (suggestionDoc.exists) {
                const suggestionData = suggestionDoc.data();
                const initiatedBy = suggestionData?.initiated_by;
                
                // Only show if initiated_by field exists AND matches current user
                if (!initiatedBy) {
                    // console.log(`Suggestion ${suggestionId} has no initiated_by field - hiding notification`);
                    return false;
                }
                
                const shouldShow = initiatedBy === currentUserID;
                // console.log(`Suggestion ${suggestionId} initiated_by: ${initiatedBy}, current user: ${currentUserID}, should show: ${shouldShow}`);
                return shouldShow;
            } else {
                // console.log(`Suggestion document ${suggestionId} not found - hiding notification`);
            }
        } catch (error) {
            console.error('Error checking lambda notification ownership:', error);
        }
        
        return false;
    }

    export async function getNotifications(customerID: string | null, limit: number = 10, currentUserID?: string | null): Promise<Notification[]> {
        const notifications: Notification[] = [];

        try {
            const companyID = await checkForCompanyID(customerID);

            if (!companyID) {
                console.error('Customer ID not found');
                return [];
            }

            const tenancyNotificationsRef = db.collection('customers')
                .doc(companyID)
                .collection('notifications')
                .doc('tenancyNotifications');

            const docSnapshot = await tenancyNotificationsRef.get();

            if (docSnapshot.exists) {
                const data = docSnapshot.data();
                if (data && data.notifications && currentUserID) {
                    // console.log(`Processing ${data.notifications.length} notifications for user ${currentUserID}`);
                    
                    // Filter notifications with async lambda notification check
                    const notificationPromises = data.notifications.map(async (notif: Notification) => {
                        // console.log(`Processing notification: ${notif.title} (userID: ${notif.userID})`);
                        
                        if (!notif.userID || notif.userID === currentUserID) {
                            // console.log(`Showing global/user notification: ${notif.title}`);
                            return notif; // Show global or user-specific notifications
                        }
                        
                        if (notif.userID === 'lambda-service-account') {
                            const shouldShow = await shouldShowLambdaNotification(notif, currentUserID, companyID);
                            if (shouldShow) {
                                // console.log(`Showing lambda notification: ${notif.title}`);
                            } else {
                                // console.log(`Hiding lambda notification: ${notif.title}`);
                            }
                            return shouldShow ? notif : null;
                        }
                        
                        // console.log(`Hiding other user notification: ${notif.title}`);
                        return null; // Don't show other users' notifications
                    });
                    
                    const filteredResults = await Promise.all(notificationPromises);
                    const filteredNotifications = filteredResults.filter(Boolean);
                    // console.log(`Final filtered count: ${filteredNotifications.length} notifications`);
                    notifications.push(...filteredNotifications.slice(0, limit));
                }
            }

            // console.log('Notifications retrieved:', notifications);
            return notifications;
        } catch (error) {
            console.error('Error retrieving notifications:', error);
            return [];
        }
    }

    export function listenForNewNotifications(customerID: string | null, callback: (notifications: Notification[]) => void, currentUserID?: string | null): void {
        checkForCompanyID(customerID).then(companyID => {
            if (!companyID) {
                console.error('Customer ID not found');
                return;
            }

            const tenancyNotificationsRef = db.collection('customers')
                .doc(companyID)
                .collection('notifications')
                .doc('tenancyNotifications');

            tenancyNotificationsRef.onSnapshot(async snapshot => {
                if (snapshot.exists) {
                    const data = snapshot.data();
                    if (data && data.notifications && Object.keys(data.notifications).length > 0 && currentUserID) {
                        // Filter notifications with async lambda notification check
                        const notificationPromises = data.notifications.map(async (notif: Notification) => {
                            if (!notif.userID || notif.userID === currentUserID) {
                                return notif; // Show global or user-specific notifications
                            }
                            
                            if (notif.userID === 'lambda-service-account') {
                                const shouldShow = await shouldShowLambdaNotification(notif, currentUserID, companyID);
                                return shouldShow ? notif : null;
                            }
                            
                            return null; // Don't show other users' notifications
                        });
                        
                        const filteredResults = await Promise.all(notificationPromises);
                        const userNotifications = filteredResults.filter(Boolean);
                        
                        const unreadNotifications = userNotifications.filter((notif: Notification) => !notif.read);
                        const readNotifications = userNotifications.filter((notif: Notification) => notif.read);

                        const unreadCount = unreadNotifications.length;

                        // Log when a new notification is viewed
                        if (unreadCount > 0) {
                            // console.log('New notifications received. Unread count:', unreadCount);
                        }

                        updateNotificationBadge(unreadCount);

                        callback([...unreadNotifications, ...readNotifications]);  // Pass both sections together
                    }
                }
            }, error => {
                console.error('Error with real-time listener:', error);
            });
        });
    }

    // Function to update the UI with a red dot and the unread notification count
    function updateNotificationBadge(unreadCount: number): void {
        const companyImg = document.querySelector('.avatar-img'); // Ensure the selector is correct
        if (companyImg) {
            let badge = companyImg.parentElement.querySelector('.notification-badge');

            if (unreadCount > 0) {
                if (!badge) {
                    badge = document.createElement('div');
                    badge.classList.add('notification-badge');
                    companyImg.parentElement.appendChild(badge);
                }
                badge.textContent = unreadCount.toString();
                (badge as HTMLElement).style.display = 'block';  // Ensure the badge is visible
            } else if (badge) {
                badge.remove();
            }
        }
    }

    // Function to mark a notification as read and log the view
    export async function markNotificationAsRead(customerID: string | null, notificationId: string, callback: (notifications: Notification[]) => void): Promise<void> {
        const companyID = await checkForCompanyID(customerID);

        if (!companyID) {
            console.error('Customer ID not found');
            return;
        }

        const tenancyNotificationsRef = db.collection('customers')
            .doc(companyID)
            .collection('notifications')
            .doc('tenancyNotifications');

        try {
            const docSnapshot = await tenancyNotificationsRef.get();
            if (docSnapshot.exists) {
                const data = docSnapshot.data();
                const notifications = data?.notifications || [];

                // Find the notification by ID and mark it as read
                const notificationToUpdate = notifications.find((notif: Notification) => notif.id === notificationId);

                if (notificationToUpdate) {
                    notificationToUpdate.read = true;

                    // Update Firestore with the read notification
                    await tenancyNotificationsRef.update({
                        notifications
                    });
                    // console.log('Notification marked as read');

                    // After updating, force UI to refresh
                    callback(notifications); // Re-render the notifications with the updated list
                }
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    }

    export async function checkAndAddSOANotification(customerID: string | null = null): Promise<void> {
        const companyID = await checkForCompanyID(customerID);

        if (!companyID) {
            console.error('Customer ID not found');
            return;
        }

        try {
            // Wait for additives and companySettings to be available
            await waitForWindowProperty('additives');
            
            // Additional check to ensure companySettings exists
            if (!window.additives?.companySettings) {
                console.log('Company settings not yet loaded');
                return;
            }

            // Check if ISM is enabled
            const ismEnabled = window.additives.companySettings?.complianceModules?.ism?.Enabled;
            if (ismEnabled !== true) {
                console.log('ISM is not enabled');
                return;
            }

            const companyRef = db.collection('customers').doc(companyID);
            const tenancyNotificationsRef = companyRef.collection('notifications').doc('tenancyNotifications');
            const customerDocRef = companyRef;

            // Fetch customer's SOA updates
            const customerDocSnapshot = await customerDocRef.get();
            const customerData = customerDocSnapshot.data();
            const soaUpdates = customerData?.soaUpdates || {};

            // Fetch updates from the updates collection
            const updatesRef = db.collection('compliance').doc('ism').collection('updates');
            const updatesSnapshot = await updatesRef.get();
            const updateDocIds = updatesSnapshot.docs.map(doc => doc.id);

            // Identify missing updates
            const missingIds = updateDocIds.filter(id => !soaUpdates[id]);

            // Fetch existing notifications
            const notificationsSnapshot = await tenancyNotificationsRef.get();
            const existingNotifications: Notification[] = notificationsSnapshot.exists ? notificationsSnapshot.data()?.notifications || [] : [];

            // Find existing SOA notification
            const soaNotification = existingNotifications.find((notif: Notification) => notif.id === "soa-update-notification");

            // Only add notification if there are missing updates and no existing unread SOA notification
            if (missingIds.length > 0) {
                if (!soaNotification) {
                    // No existing SOA notification, add it
                    const notification: Notification = {
                        id: "soa-update-notification",
                        icon: "fa fa-duotone fa-solid fa-party-horn",
                        title: "Woo! An update to your SOA is available.",
                        body: "Click to be taken to the Update Portal.",
                        timestamp: new Date(),
                        read: false
                    };

                    // Add the new notification
                    const updatedNotifications = [...existingNotifications, notification];
                    await tenancyNotificationsRef.set({ notifications: updatedNotifications });
                    // console.log('SOA update notification added successfully');
                } else if (soaNotification.read) {
                    // Existing SOA notification is read, mark it as unread
                    const updatedNotifications = existingNotifications.map((notif: Notification) => {
                        if (notif.id === soaNotification.id) {
                            return { ...notif, read: false, timestamp: new Date() };
                        }
                        return notif;
                    });
                    await tenancyNotificationsRef.set({ notifications: updatedNotifications });
                    // console.log('SOA notification marked as unread');
                }
                // If the notification exists and is unread, do nothing
            } else {
                // No missing updates, remove existing SOA notification if any
                if (soaNotification) {
                    soaNotification.read = true;
                    const updatedNotifications = existingNotifications.filter((notif: Notification) => notif.id !== soaNotification.id);
                    updatedNotifications.push(soaNotification);
                    await tenancyNotificationsRef.set({ notifications: updatedNotifications });
                    // console.log('All updates seen, SOA notification removed');
                }
            }
        } catch (error) {
            console.error('Error in checkAndAddSOANotification:', error);
        }
    }

    // Function to handle marking the notification as read and redirecting
    export async function markNotificationAsReadAndRedirect(customerID: string | null, notificationId: string): Promise<void> {
        const companyID = await checkForCompanyID(customerID);

        if (!companyID) {
            console.error('Customer ID not found');
            return;
        }

        const tenancyNotificationsRef = db.collection('customers')
            .doc(companyID)
            .collection('notifications')
            .doc('tenancyNotifications');
        const customerDocRef = db.collection('customers').doc(companyID);

        try {
            const docSnapshot = await tenancyNotificationsRef.get();
            if (docSnapshot.exists) {
                const data = docSnapshot.data();
                const notifications = data?.notifications || [];

                // Find the notification by ID and mark it as read
                const notificationToUpdate = notifications.find((notif: Notification) => notif.id === notificationId);

                if (notificationToUpdate) {
                    notificationToUpdate.read = true;

                    // Update Firestore with the read notification
                    await tenancyNotificationsRef.update({
                        notifications
                    });
                    // console.log('Notification marked as read');

                    // Now, update the soaUpdates map after the user clicks the notification
                    const customerDocSnapshot = await customerDocRef.get();
                    const customerData = customerDocSnapshot.data();
                    const soaUpdates = customerData?.soaUpdates || {};

                    // Get the list of document IDs from /compliance/ism/updates collection
                    const updatesRef = db.collection('compliance').doc('ism').collection('updates');
                    const updatesSnapshot = await updatesRef.get();
                    const updateDocIds = updatesSnapshot.docs.map(doc => doc.id);

                    // Update the soaUpdates map with the missing IDs
                    const updatedSOA = { ...soaUpdates };
                    updateDocIds.forEach(id => {
                        updatedSOA[id] = true;  // Mark the update as seen
                    });

                    // Save the updated soaUpdates map
                    await customerDocRef.update({
                        soaUpdates: updatedSOA
                    });
                    // console.log('SOA updates map updated');

                    // Redirect the user to the SOA updates page
                    window.location.href = 'soa-updates.html'; // Redirect to the SOA updates page
                }
            }
        } catch (error) {
            console.error('Error marking notification as read and redirecting:', error);
        }
    }

    export async function markAllNotificationsAsRead(customerID: string | null, callback?: (notifications: Notification[]) => void): Promise<void> {
        const companyID = await checkForCompanyID(customerID);

        if (!companyID) {
            console.error('Customer ID not found');
            return;
        }

        const tenancyNotificationsRef = db.collection('customers')
            .doc(companyID)
            .collection('notifications')
            .doc('tenancyNotifications');

        try {
            const docSnapshot = await tenancyNotificationsRef.get();
            if (docSnapshot.exists) {
                const data = docSnapshot.data();
                const notifications = data?.notifications || [];

                // Mark all notifications as read
                const updatedNotifications = notifications.map((notif: Notification) => {
                    return { ...notif, read: true };
                });

                // Update Firestore with the read notifications
                await tenancyNotificationsRef.update({
                    notifications: updatedNotifications
                });
                // console.log('All notifications marked as read');

                // Update UI if callback provided
                if (callback) {
                    callback(updatedNotifications);
                }
            }
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    }

    // Corrected displayNotifications function
    export function displayNotifications(notifications: Notification[], containerId: string = "notifications", showAll: boolean = false): void {
        const container = document.getElementById(containerId);
        if (!container) {
            console.error('Notification container not found');
            return;
        }

        // Separate notifications into unread and read
        const unreadNotifications = notifications.filter((notif: Notification) => !notif.read);
        const readNotifications = notifications.filter((notif: Notification) => notif.read);

        container.innerHTML = ''; // Clear the previous notifications

        // Display unread notifications
        if (unreadNotifications.length > 0) {
            const unreadSection = document.createElement('div');
            unreadSection.classList.add('notifications-section');
            unreadSection.innerHTML = '<div class="inset-title">Unread Notifications</div>';
            unreadNotifications.forEach((notification) => {
                const notificationDiv = document.createElement('div');
                notificationDiv.classList.add('notification');

                if (notification.title === "Woo! An update to your SOA is available.") {
                    // SOA update notification: Add onclick to mark as read and redirect
                    notificationDiv.innerHTML = `
                <div class="option" onclick="window.NotificationSystem.markNotificationAsReadAndRedirect(null, '${notification.id}')">
                    ${notification.icon ? '<div class="option-icon notifications"><i class="' + notification.icon + '"></i></div>' : ''}
                    <div class="option-text">
                        <div class="option-title">${notification.title}</div>
                        <div class="option-descriptor">${notification.body}</div>
                    </div>
                </div>
                `;
                } else {
                    // Non-SOA notification: Add onclick to mark as read
                    notificationDiv.innerHTML = `
                <div class="option" onclick="window.NotificationSystem.markNotificationAsRead(null, '${notification.id}', window.NotificationSystem.displayNotifications)">
                    ${notification.icon ? '<div class="option-icon notifications"><i class="' + notification.icon + '"></i></div>' : ''}
                    <div class="option-text">
                        <div class="option-title">${notification.title}</div>
                        <div class="option-descriptor">${notification.body}</div>
                    </div>
                </div>
                `;
                }

                unreadSection.appendChild(notificationDiv);
            });
            container.appendChild(unreadSection);
        }

        // Display read notifications
        if (readNotifications.length > 0) {
            const readSection = document.createElement('div');
            readSection.classList.add('notifications-section');
            readSection.innerHTML = '<div class="inset-title">Read Notifications</div>';

            // Limit to 5 notifications if showAll is false
            const readNotificationsToDisplay = showAll ? readNotifications : readNotifications.slice(0, 5);

            readNotificationsToDisplay.forEach((notification) => {
                const notificationDiv = document.createElement('div');
                notificationDiv.classList.add('notification');

                if (notification.title === "Woo! An update to your SOA is available.") {
                    // SOA update notification: Add onclick to redirect
                    notificationDiv.innerHTML = `
                <div class="option" onclick="window.NotificationSystem.markNotificationAsReadAndRedirect(null, '${notification.id}')">
                    ${notification.icon ? '<div class="option-icon notifications"><i class="' + notification.icon + '"></i></div>' : ''}
                    <div class="option-text">
                        <div class="option-title">${notification.title}</div>
                        <div class="option-descriptor">${notification.body}</div>
                    </div>
                </div>
                `;
                } else {
                    notificationDiv.innerHTML = `
                    <div class="option">
                        ${notification.icon ? '<div class="option-icon notifications"><i class="' + notification.icon + '"></i></div>' : ''}
                        <div class="option-text">
                            <div class="option-title">${notification.title}</div>
                            <div class="option-descriptor">${notification.body}</div>
                        </div>
                    </div>
                    `;
                }
                readSection.appendChild(notificationDiv);
            });

            // Add "See all notifications" link if there are more than 5 and showAll is false
            if (readNotifications.length > 5 && !showAll) {
                const seeAllLink = document.createElement('div');
                seeAllLink.classList.add('see-all-notifications');
                seeAllLink.textContent = 'See all notifications';

                // Corrected the onclick handler to prevent dropdown from closing
                seeAllLink.addEventListener('click', function (event) {
                    event.stopPropagation(); // Prevent dropdown from closing
                    // Display all notifications
                    NotificationSystem.displayNotifications(notifications, containerId, true);
                });

                readSection.appendChild(seeAllLink);
            }

            container.appendChild(readSection);
        }
    }
}